import { Layout } from "@layouts";
import { Bar } from "@organisms";
import { PageBreadcrumbs } from "@molecules";
import { useState, useContext, useMemo } from "react";
import { GiftCardOffer, PagePaths } from "../../../../types";
import { PremiumUser } from "@api";
import { AuthContext } from "@auth";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input as SInput,
  Checkbox,
  Badge,
} from "@shadcn";
import { Check, Clock, X } from "lucide-react";
import { useTranslation } from "react-i18next";

export type AirdropSelectMembersPageTemplateProps = {
  premiumUsers: any[];
  giftCardOffers: GiftCardOffer[] | null;
  progress: number;
  onContinuePressed: (data: PremiumUser) => void;
  onReturnPressed: () => void;
};

export const AirdropSelectMembersPageTemplate = ({
  premiumUsers,
  onContinuePressed,
  onReturnPressed,
}: AirdropSelectMembersPageTemplateProps): JSX.Element => {
  const { setSelectedPageIndex } = useContext(AuthContext);
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedRowIndex, setSelectedRowIndex] = useState<number | null>(null);
  const navigate = useNavigate();

  const goToPerks = () => {
    setSelectedPageIndex?.(3);
    navigate(`/dashboard/${PagePaths.perks}`);
  };
  const goToAirdrops = () => {
    setSelectedPageIndex?.(3);
    navigate(`/dashboard/${PagePaths.airdrop}/main`);
  };

  const filteredUsers = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return premiumUsers
      .map((user, originalIndex) => ({ user, originalIndex }))
      .filter(({ user }) => {
        if (!query) return true;
        const email = (user.emailAddress ?? "").toLowerCase();
        const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.toLowerCase();
        return email.includes(query) || name.includes(query);
      });
  }, [premiumUsers, searchTerm]);

  const handleRowSelection = (rowIndex: number) => {
    setSelectedRowIndex((prev) => (prev === rowIndex ? null : rowIndex));
  };

  const formatDate = (value: string | Date | null | undefined) => {
    if (!value) return "--";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "--";
    }
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  };

  const handleContinue = () => {
    if (selectedRowIndex === null) {
      return;
    }
    onContinuePressed(premiumUsers[selectedRowIndex]);
  };

  return (
    <Layout
      variant="dashboard"
      header={t("airdrop.common.header")}
      subheader={
        <div className="flex flex-row items-start justify-between max-w-5xl">
          <div className="page-subtitle mt-2">
            {t("airdrop.common.subheader")}
          </div>
        </div>
      }
    >
      <div className="mr-4 rounded-md max-w-5xl">
        <PageBreadcrumbs
          items={[
            { label: t("airdrop.common.breadcrumbs.benefits"), onClick: goToPerks },
            { label: t("airdrop.common.breadcrumbs.airdrops"), onClick: goToAirdrops },
            { label: t("airdrop.common.breadcrumbs.selectMembers") },
          ]}
        />

        <div className="flex flex-col gap-6 border border-neutral-light bg-white rounded-2xl w-full min-w-[400px] p-6">
          <div>
            <h2 className="text-xl font-normal">
              <div className="flex items-center gap-3 leading-none p-[5px]">
                <span className="flex items-center justify-center w-11 h-11 rounded-full bg-white border border-neutral-light text-2xl leading-none">
                  1.
                </span>
                <span className="flex items-center gap-2 leading-none">
                  {t("airdrop.selectMembers.title")}
                </span>
              </div>
            </h2>
          </div>
          {/* Below we'll need an if-else statement to either display employees or display smth to say no employees are added */}
          {premiumUsers.length ? (
            <div className="">
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between pb-4">
              <div className="relative w-full md:w-1/3">
                <SInput
                  placeholder={t("airdrop.selectMembers.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  className="w-full pr-10 input-focus-primary"
                />
                  {searchTerm ? (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      className="absolute inset-y-0 right-3 flex items-center text-neutral-medium hover:text-neutral-dark focus:outline-none"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  ) : null}
                </div>
              </div>
              <div className="rounded-lg border border-neutral-light overflow-hidden">
                <Table className="border-collapse">
                  <TableHeader className="bg-neutral-lightest">
                      <TableRow className="border-b border-neutral-light">
                        <TableHead className="pl-[10px] px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider w-14">
                        {t("airdrop.selectMembers.tableHeaders.select")}
                      </TableHead>
                      <TableHead className="px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("airdrop.selectMembers.tableHeaders.name")}
                      </TableHead>
                      <TableHead className="px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("airdrop.selectMembers.tableHeaders.email")}
                      </TableHead>
                      <TableHead className="px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("airdrop.selectMembers.tableHeaders.workAnniversary")}
                      </TableHead>
                      <TableHead className="px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("airdrop.selectMembers.tableHeaders.birthday")}
                      </TableHead>
                      <TableHead className="px-4 text-xs font-medium text-neutral-mediumDark uppercase tracking-wider">
                        {t("airdrop.selectMembers.tableHeaders.status")}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.length ? (
                      filteredUsers.map(({ user, originalIndex }, position) => {
                        const isLastRow = position === filteredUsers.length - 1;
                        const isSelected = selectedRowIndex === originalIndex;
                        const isInProgress = !user.userId;
                        const statusConfig = isInProgress
                          ? {
                              label: t("airdrop.selectMembers.status.inProgress"),
                              badgeClass:
                                "bg-white border-neutral-light text-neutral-dark",
                              iconWrapperClass: "bg-neutral-light text-neutral-dark h-4 w-4",
                              Icon: Clock,
                            }
                          : {
                              label: t("airdrop.selectMembers.status.onboarded"),
                              badgeClass: "bg-white border-green-200 text-green-700",
                              iconWrapperClass: "bg-green-500 text-white h-3 w-3",
                              Icon: Check,
                            };

                        return (
                          <TableRow
                            key={`${user.emailAddress}-${originalIndex}`}
                            className={`border-b-0 hover:bg-primary-lightest ${isSelected ? "bg-primary-lightest" : ""}`}
                            onClick={() => handleRowSelection(originalIndex)}
                          >
                            <TableCell
                              className={`pl-[10px] px-4 ${
                                isLastRow ? "" : "border-b border-neutral-light"
                              }`}
                            >
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={(value) => {
                                  handleRowSelection(originalIndex);
                                }}
                                onClick={(event) => event.stopPropagation()}
                                aria-label={t("airdrop.selectMembers.ariaSelect", { email: user.emailAddress })}
                                className="airdrops-checkbox h-5 w-5 rounded-full border-2 border-neutral-medium2 bg-white focus-visible:ring-0 focus-visible:ring-offset-0"
                              />
                            </TableCell>
                            <TableCell
                              className={`px-4 text-sm font-medium text-neutral-darkest ${
                                isLastRow ? "" : "border-b border-neutral-light"
                              }`}
                            >
                              {`${user.firstName ?? ""} ${user.lastName ?? ""}`.trim() ||
                                "--"}
                            </TableCell>
                            <TableCell
                              className={`px-4 text-xs text-neutral-dark ${
                                isLastRow ? "" : "border-b border-neutral-light"
                              }`}
                            >
                              {user.emailAddress || "--"}
                            </TableCell>
                            <TableCell
                              className={`px-4 text-xs text-neutral-dark ${
                                isLastRow ? "" : "border-b border-neutral-light"
                              }`}
                            >
                              {formatDate(user.employmentStartDate)}
                            </TableCell>
                            <TableCell
                              className={`px-4 text-xs text-neutral-dark ${
                                isLastRow ? "" : "border-b border-neutral-light"
                              }`}
                            >
                              {formatDate(user.birthday)}
                            </TableCell>
                            <TableCell
                              className={`px-4 ${
                                isLastRow ? "" : "border-b border-neutral-light"
                              }`}
                            >
                              <Badge
                                variant="outline"
                                className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium ${statusConfig.badgeClass}`}
                              >
                                <span
                                  className={`flex items-center justify-center rounded-full ${statusConfig.iconWrapperClass}`}
                                >
                                  <statusConfig.Icon
                                    className={isInProgress ? "h-2.5 w-2.5" : "h-1.5 w-1.5"}
                                  />
                                </span>
                                <span>{statusConfig.label}</span>
                              </Badge>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={6}
                          className="px-[10px] h-24 text-center text-sm text-neutral-medium"
                        >
                          {t("airdrop.selectMembers.emptySearch")}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-start py-1">
              <div className="text-center text-gray-500">
                <p className="text-base">{t("airdrop.selectMembers.emptyState")}</p>
              </div>
            </div>
          )}
        </div>
        <Bar
          variant="bottom"
          onReturnPressed={onReturnPressed}
          onContinuePressed={handleContinue}
          isContinueDisabled={selectedRowIndex === null}
        />
      </div>
    </Layout>
  );
};
